import React, { useState, useEffect } from 'react';
import { Form, Table, Alert, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import RendicontazioneAdminService from '../../services/RendicontazioneAdminService/RendicontazioneAdminService';

const ANNO_INIZIO = 2025;

/**
 * Calcola l'importo della rendicontazione in base al totale corsi attivati.
 * Scaglioni di base: 1–100 = €10, 101–200 = €20, 201–350 = €30, 351–500 = €40, oltre 500 = €50.
 * Da ogni scaglione va detratto € 10,00 sconto alla fonte → netti: €0, €10, €20, €30, €40.
 */
function calcolaRendicontazione(totaleCorsi) {
    if (totaleCorsi <= 0) return 0;
    let importo = 0;
    if (totaleCorsi > 500) {
        importo += (totaleCorsi - 500) * 40;  // netto €40
        totaleCorsi = 500;
    }
    if (totaleCorsi > 350) {
        importo += (totaleCorsi - 350) * 30;  // netto €30
        totaleCorsi = 350;
    }
    if (totaleCorsi > 200) {
        importo += (totaleCorsi - 200) * 20;  // netto €20
        totaleCorsi = 200;
    }
    if (totaleCorsi > 100) {
        importo += (totaleCorsi - 100) * 10;  // netto €10
        totaleCorsi = 100;
    }
    // 1–100: netto €0 (base €10 − €10 sconto)
    return importo;
}

export default function LiquidazioneAnnualePage() {
    const user = useSelector((state) => state.user.userData);
    const currentYear = new Date().getFullYear();
    const [anno, setAnno] = useState(currentYear);
    const [listaPartner, setListaPartner] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const idSuperpartner = user?.id_superpartner;
    const nomeSuperpartner = user?.nome || user?.username || '';

    const anniOptions = [];
    for (let y = ANNO_INIZIO; y <= currentYear; y++) {
        anniOptions.push(y);
    }

    useEffect(() => {
        if (!idSuperpartner || !anno) {
            setListaPartner([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        RendicontazioneAdminService.getLiquidazioneAnnualeSuperpartner(idSuperpartner, anno)
            .then((data) => {
                if (!cancelled) {
                    setListaPartner(Array.isArray(data) ? data : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Errore nel caricamento della liquidazione annuale.');
                    setListaPartner([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [idSuperpartner, anno]);

    if (idSuperpartner == null || idSuperpartner === '') {
        return (
            <div className="p-4">
                <h5 className="mb-3">Liquidazione annuale</h5>
                <Alert variant="warning">Non sei associato a un superpartner. Impossibile visualizzare la liquidazione annuale.</Alert>
            </div>
        );
    }
    if (idSuperpartner != 2) {
        return (
            <div className="p-4">
                <h5 className="mb-3">Liquidazione annuale</h5>
                <Alert variant="info">La liquidazione annuale è disponibile solo per il superpartner autorizzato.</Alert>
            </div>
        );
    }

    const totaleCorsi = listaPartner.reduce((sum, p) => sum + (parseInt(p.num_corsi_attivati, 10) || 0), 0);
    const importoRendicontazione = calcolaRendicontazione(totaleCorsi);

    return (
        <div className="p-4">
            <h5 className="mb-3">Liquidazione annuale – {nomeSuperpartner}</h5>

            <Form.Group className="mb-3">
                <Form.Label>Anno</Form.Label>
                <Form.Select
                    value={anno}
                    onChange={(e) => setAnno(parseInt(e.target.value, 10))}
                    disabled={loading}
                >
                    {anniOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Caricamento...
                </div>
            ) : (
                <>
                    <Table striped bordered hover size="sm" className="mb-4">
                        <thead className="table-light">
                            <tr>
                                <th>Partner associati</th>
                                <th className="text-end" style={{ width: '180px' }}>Corsi Attivati (anno {anno})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listaPartner.length === 0 ? (
                                <tr>
                                    <td colSpan={2} className="text-center text-muted">Nessun partner o nessun dato per l'anno selezionato.</td>
                                </tr>
                            ) : (
                                listaPartner.map((row) => (
                                    <tr key={row.id_agenzia}>
                                        <td>{row.denominazione || '-'}</td>
                                        <td className="text-end">{row.num_corsi_attivati ?? 0}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    <div className="border rounded p-3 bg-light">
                        <h6 className="mb-2">Rendicontazione (corso Addetto ai servizi di controllo)</h6>
                        <p className="small text-muted mb-2">
                            Prezzo contrattuale del corso: <strong>€ 300,00</strong>. Federpol ha applicato in partenza uno sconto di <strong>€ 10,00 a corso</strong> a tutti i partner (sconto alla fonte). Dagli importi per scaglione va quindi detratto € 10,00 a corso per ottenere l'importo netto liquidabile.
                        </p>
                        <p className="small text-muted mb-2">
                            Totale corsi attivati nell'anno: <strong>{totaleCorsi}</strong>
                        </p>
                        <p className="small mb-1"><strong>Scaglioni (lordo − € 10,00 sconto = netto):</strong></p>
                        <ul className="small mb-1">
                            <li><strong>Da 1 corso a 100 corsi:</strong> € 10,00 cad. − € 10,00 sconto = <strong>€ 0,00 cad.</strong></li>
                            <li><strong>Da 101 corsi a 200 corsi:</strong> € 20,00 cad. − € 10,00 sconto = <strong>€ 10,00 cad.</strong></li>
                            <li><strong>Da 201 corsi a 350 corsi:</strong> € 30,00 cad. − € 10,00 sconto = <strong>€ 20,00 cad.</strong></li>
                            <li><strong>Da 351 corsi a 500 corsi:</strong> € 40,00 cad. − € 10,00 sconto = <strong>€ 30,00 cad.</strong></li>
                            <li><strong>Oltre i 500 corsi:</strong> € 50,00 cad. − € 10,00 sconto = <strong>€ 40,00 cad.</strong></li>
                        </ul>
                        <p className="mb-0 mt-2">
                            <strong>Importo liquidazione annuale: € {importoRendicontazione.toFixed(2)}</strong>
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}
